import MyLocationIcon from '@mui/icons-material/MyLocation';
import { Box, Card, Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import ReactWordcloud, { Word } from 'react-wordcloud';
import {
  getTopSkillsAndJobTitles,
  getCandidateTopSkillsAndJobTitles,
  ILocation,
} from 'wisestep-api';
import { WsButton, WsColors, WsFilterDialog, WsLocationSearch, WsTitleBanner } from 'wisestep-base';

import { SkillsMatrixTable } from '../helper/analyticsTableHelper';

import AnalyticsCountTable from './AnalyticsCountTable';

export interface ISkillsCount {
  skill: string;
  count: number;
}

export interface IJobTitleCount {
  jobTitle: string;
  count: number;
}

interface ISkillsMatrixData {
  topSkills: ISkillsCount[];
  topJobTitles: IJobTitleCount[];
}

const SkillsMatrix = () => {
  const location = useLocation();
  const methods = useForm<{ locations: ILocation[] }>({ defaultValues: { locations: [] } });
  const { handleSubmit, reset } = methods;
  const [skillsMatrixData, setSkillsMatrixData] = useState<ISkillsMatrixData>({
    topSkills: [],
    topJobTitles: [],
  });
  const skillClouds = skillsMatrixData.topSkills.map(
    (skill) =>
      ({
        text: skill.skill,
        value: skill.count,
      } as Word)
  );

  const fetchData = useCallback(
    async (searchFilter = {}) => {
      try {
        const response = location.pathname.includes('demand')
          ? await getTopSkillsAndJobTitles(searchFilter)
          : await getCandidateTopSkillsAndJobTitles(searchFilter);
        response.data && setSkillsMatrixData(response.data);
      } catch (e) {
        console.error('error fetching analtyics data', e);
      }
    },
    [location.pathname]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmit = (formState) => {
    fetchData(formState);
  };

  const cloudOptions = {
    rotations: 0,
    rotationAngles: [0, 0],
    fontSizes: [20, 60],
    enableTooltip: true,
    scale: 'sqrt',
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <WsTitleBanner
          title={location.pathname.includes('demand') ? 'Job Demand Matrix' : 'Talent Matrix'}
          backButton={false}
        />
      </Grid>
      <Grid item xs={12}>
        <FormProvider {...methods}>
          <Grid
            container
            component="form"
            bgcolor={WsColors.White}
            p={2}
            borderRadius={1.5}
            justifyContent={'space-between'}
            onSubmit={handleSubmit(onSubmit)}
            onReset={() => {
              reset();
              fetchData({ locations: [] });
            }}
          >
            <Grid item xs={2}>
              <WsFilterDialog
                name={
                  location.pathname.includes('demand') ? 'Job Locations' : 'Candidate Locations'
                }
                label="Location"
                icon={<MyLocationIcon />}
              >
                <Box width={400} p={2}>
                  <WsLocationSearch placeHolder="Location" name="locations" />
                </Box>
              </WsFilterDialog>
            </Grid>
            <Grid item container xs={2} justifyContent={'space-evenly'}>
              <WsButton type="submit">Apply</WsButton>
              <WsButton type="reset">Clear</WsButton>
            </Grid>
          </Grid>
        </FormProvider>
      </Grid>
      <Grid item xs={12}>
        <Card>
          <ReactWordcloud words={skillClouds} options={cloudOptions as any} />
        </Card>
      </Grid>
      <Grid item xs={6} height={400}>
        <Card sx={{ height: '100%' }}>
          <AnalyticsCountTable
            tableType={SkillsMatrixTable.TOP_SKILLS}
            data={skillsMatrixData.topSkills}
          />
        </Card>
      </Grid>
      <Grid item xs={6} height={400}>
        <Card sx={{ height: '100%' }}>
          <AnalyticsCountTable
            tableType={SkillsMatrixTable.TOP_JOB_TITLES}
            data={skillsMatrixData.topJobTitles}
          />
        </Card>
      </Grid>
    </Grid>
  );
};

export default SkillsMatrix;
